import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from './AuthContext';
import { getLevel, getProgressToNextLevel } from '../services/xpService';

interface GamificationContextType {
    xp: number;
    totalScore: number;
    offensiveScore: number;
    level: number;
    progressToNextLevel: number;
    addXP: (amount: number) => Promise<void>;
    getXP: () => number;
    getLevel: () => number;
    getProgressToNextLevel: () => number;
    loading: boolean;
}

const GamificationContext = createContext<GamificationContextType>({
    xp: 0,
    totalScore: 0,
    offensiveScore: 0,
    level: 1,
    progressToNextLevel: 0,
    addXP: async () => { },
    getXP: () => 0,
    getLevel: () => 1,
    getProgressToNextLevel: () => 0,
    loading: true,
});

export const useGamification = () => useContext(GamificationContext);

export const GamificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [xp, setXp] = useState(0);
    const [totalScore, setTotalScore] = useState(0);
    const [offensiveScore, setOffensiveScore] = useState(0);
    const [loading, setLoading] = useState(true);

    // Função para recalcular Pontuação Ofensiva baseada na semana atual
    // IMPORTANTE: Esta é uma pontuação SEMANAL, calculada exclusivamente da tabela daily_progress
    // Soma os pontos dos últimos 7 dias e limita a 100
    const recalculateOffensiveScore = useCallback(async () => {
        if (!user) return;

        try {
            // Calcular pontuação baseada nos últimos 7 dias
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - 6); // Últimos 7 dias (incluindo hoje)
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);

            const startDateStr = startDate.toISOString().split('T')[0];
            const endDateStr = endDate.toISOString().split('T')[0];

            const { data: progressData, error } = await supabase
                .from('daily_progress')
                .select('points, date')
                .eq('user_id', user.id)
                .gte('date', startDateStr)
                .lte('date', endDateStr)
                .order('date', { ascending: true });

            if (error) {
                console.error('Erro ao buscar dados para ofensiva:', error);
                return;
            }

            // Somar os pontos dos últimos 7 dias
            let totalPoints = 0;
            
            if (progressData && progressData.length > 0) {
                console.log('Dados de progresso encontrados para ofensiva:', progressData);
                // Criar um mapa de datas para garantir que cada dia seja contado apenas uma vez
                const dayPoints = new Map<string, number>();
                
                progressData.forEach((day: { date: string; points: number | null }) => {
                    const points = day.points || 0;
                    console.log(`Dia ${day.date}: ${points} pontos`);
                    // Usar a maior pontuação do dia (caso haja múltiplos registros)
                    const existingPoints = dayPoints.get(day.date) || 0;
                    dayPoints.set(day.date, Math.max(existingPoints, points));
                });
                
                // Somar todas as pontuações dos dias
                dayPoints.forEach((points, date) => {
                    totalPoints += points;
                    console.log(`Soma parcial: ${totalPoints} (adicionado ${points} do dia ${date})`);
                });
            } else {
                console.log('Nenhum dado de progresso encontrado para ofensiva');
            }

            // Limitar a 100 pontos
            const finalScore = Math.min(totalPoints, 100);
            console.log(`Pontuação Ofensiva calculada: ${totalPoints} -> limitado a ${finalScore}`);
            
            // Atualizar estado
            setOffensiveScore(finalScore);
        } catch (error) {
            console.error('Erro ao recalcular Pontuação Ofensiva:', error);
        }
    }, [user]);

    // Função para carregar dados do banco (reutilizável)
    const loadGamificationData = useCallback(async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        try {
            // 1. Carregar XP do banco (sempre persistido, nunca resetado)
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('xp')
                .eq('id', user.id)
                .maybeSingle();

            if (profileError && profileError.code !== 'PGRST116') {
                console.error('Erro ao carregar dados de gamificação:', profileError);
            }

            const savedXp = profileData?.xp || 0;
            setXp(savedXp);

            // 2. Calcular Pontuação Total: soma de todos os pontos da tabela daily_progress
            const { data: allProgressData, error: progressError } = await supabase
                .from('daily_progress')
                .select('points, date')
                .eq('user_id', user.id);

            if (progressError) {
                console.error('Erro ao carregar pontuação total:', progressError);
            }

            let totalPointsSum = 0;
            if (allProgressData && allProgressData.length > 0) {
                console.log('Dados de progresso encontrados para pontuação total:', allProgressData.length, 'registros');
                // Criar um mapa de datas para garantir que cada dia seja contado apenas uma vez
                const dayPoints = new Map<string, number>();
                
                allProgressData.forEach((day: { date: string; points: number | null }) => {
                    const points = day.points || 0;
                    // Usar a maior pontuação do dia (caso haja múltiplos registros, o que não deveria acontecer)
                    const existingPoints = dayPoints.get(day.date) || 0;
                    dayPoints.set(day.date, Math.max(existingPoints, points));
                });
                
                // Somar todas as pontuações dos dias únicos
                dayPoints.forEach((points, date) => {
                    totalPointsSum += points;
                    console.log(`Pontuação Total: ${totalPointsSum} (adicionado ${points} do dia ${date})`);
                });
            } else {
                console.log('Nenhum dado de progresso encontrado para pontuação total');
            }

            console.log(`Pontuação Total final: ${totalPointsSum}`);
            setTotalScore(totalPointsSum);

            // 3. Calcular Pontuação Ofensiva Semanal (sempre consultar banco, nunca zerar)
            await recalculateOffensiveScore();
        } catch (error) {
            console.error('Erro ao carregar dados de gamificação:', error);
            // Em caso de erro, manter valores padrão
            setXp(0);
            setTotalScore(0);
            setOffensiveScore(0);
        } finally {
            setLoading(false);
        }
    }, [user, recalculateOffensiveScore]);

    // Carregar dados iniciais quando o componente monta ou quando o usuário muda
    useEffect(() => {
        loadGamificationData();
    }, [loadGamificationData]);

    // Escutar eventos de atualização de progresso diário
    useEffect(() => {
        const handleDailyProgressUpdate = async () => {
            // Aguardar um pouco para garantir que o banco foi atualizado
            setTimeout(async () => {
                // Recalcular tanto a ofensiva quanto a pontuação total
                await loadGamificationData();
            }, 1000); // Aumentar delay para garantir que o banco foi atualizado
        };

        window.addEventListener('dailyProgressUpdated', handleDailyProgressUpdate);
        return () => window.removeEventListener('dailyProgressUpdated', handleDailyProgressUpdate);
    }, [loadGamificationData]);
    
    // Recarregar dados quando a janela ganha foco (volta para a aba ou recarrega)
    useEffect(() => {
        if (!user) return;
        
        const handleFocus = () => {
            // Recarregar dados do banco quando a janela ganha foco
            loadGamificationData();
        };
        
        // Recarregar quando a janela ganha foco
        window.addEventListener('focus', handleFocus);
        
        // Recarregar quando a página fica visível (útil para abas do navegador)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadGamificationData();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, loadGamificationData]);

    const addXP = async (amount: number) => {
        if (!user) return;

        // Buscar valores atuais do banco para garantir que estamos somando corretamente
        try {
            const { data: currentData } = await supabase
                .from('profiles')
                .select('xp')
                .eq('id', user.id)
                .maybeSingle();

            // Usar valores do banco como base (mais confiável que o estado local)
            const currentXp = currentData?.xp || xp || 0;

            // Calcular novo valor de XP (sempre acumulativo, nunca resetado)
            const newXp = currentXp + amount;

            // Atualização otimista do estado local
            setXp(newXp);

            // Atualizar XP no banco (acumulativo, nunca resetado)
            const { error } = await supabase
                .from('profiles')
                .update({ 
                    xp: newXp
                })
                .eq('id', user.id);

            if (error) {
                // Se erro, tentar criar o perfil se não existir
                if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
                    await supabase
                        .from('profiles')
                        .upsert({ 
                            id: user.id,
                            xp: newXp
                        }, {
                            onConflict: 'id'
                        });
                } else {
                    throw error;
                }
            }

            // Recalcular pontuação total e ofensiva após adicionar XP
            // A pontuação total é recalculada a partir da tabela daily_progress
            // A pontuação ofensiva é calculada apenas com base nos últimos 7 dias
            await loadGamificationData();
        } catch (error) {
            console.error('Erro ao salvar XP:', error);
            // Recarregar dados do banco em caso de erro para garantir sincronização
            await loadGamificationData();
        }
    };

    // Calcular nível e progresso baseado no XP atual
    const level = getLevel(xp);
    const progressToNextLevel = getProgressToNextLevel(xp);

    return (
        <GamificationContext.Provider value={{ 
            xp, 
            totalScore,
            offensiveScore: Math.floor(offensiveScore), 
            level,
            progressToNextLevel,
            addXP, 
            getXP: () => xp,
            getLevel: () => level,
            getProgressToNextLevel: () => progressToNextLevel,
            loading 
        }}>
            {children}
        </GamificationContext.Provider>
    );
};
